import type { ComponentProps, ElementRef, PropsWithChildren } from "react";
import { forwardRef } from "react";
import { twMerge } from "tailwind-merge";

export const Root = forwardRef<
  ElementRef<"div">,
  ComponentProps<"div"> & PropsWithChildren
>(({ className, children, ...rest }, ref) => (
  <div className={className} ref={ref} {...rest}>
    {children}
  </div>
));
Root.displayName = "HealthDetails.Root";

type TitleProps = ComponentProps<"h3"> & PropsWithChildren;

export const Title = forwardRef<ElementRef<"h3">, TitleProps>(
  ({ className, children, ...rest }, ref) => (
    <h3
      className={twMerge("pt-10 text-2xl text-white", className)}
      ref={ref}
      {...rest}
    >
      {children}
    </h3>
  ),
);
Title.displayName = "HealthDetails.Title";

type CardsHolderProps = ComponentProps<"div"> & PropsWithChildren;

export const CardsHolder = forwardRef<ElementRef<"div">, CardsHolderProps>(
  ({ className, children, ...rest }, ref) => (
    <div
      className={twMerge("grid grid-cols-3 items-start gap-4 pt-6", className)}
      ref={ref}
      {...rest}
    >
      {children}
    </div>
  ),
);
CardsHolder.displayName = "HealthDetails.CardsHolder";

type CardProps = ComponentProps<"div"> & PropsWithChildren;

export const Card = forwardRef<ElementRef<"div">, CardProps>(
  ({ className, children, ...rest }, ref) => (
    <div
      className={twMerge(
        "w-full rounded-xl bg-surface-container-highest p-5 dark:bg-surface-container-default-dark",
        className,
      )}
      ref={ref}
      {...rest}
    >
      {children}
    </div>
  ),
);
Card.displayName = "HealthDetails.Card";

type CardHeaderProps = ComponentProps<"div"> & PropsWithChildren;

export const CardHeader = forwardRef<ElementRef<"div">, CardHeaderProps>(
  ({ className, children, ...rest }, ref) => (
    <div className={twMerge("flex gap-2 pb-2", className)} ref={ref} {...rest}>
      {children}
    </div>
  ),
);
CardHeader.displayName = "HealthDetails.CardHeader";

type CardTitleProps = ComponentProps<"p"> & PropsWithChildren;

export const CardTitle = forwardRef<ElementRef<"p">, CardTitleProps>(
  ({ className, children, ...rest }, ref) => (
    <p
      className={twMerge("text-primary-40 dark:text-primary-80", className)}
      ref={ref}
      {...rest}
    >
      {children}
    </p>
  ),
);
CardTitle.displayName = "HealthDetails.CardTitle";

type CardBodyProps = ComponentProps<"div"> & PropsWithChildren;

export const CardBody = forwardRef<ElementRef<"div">, CardBodyProps>(
  ({ className, children, ...rest }, ref) => (
    <div
      className={twMerge("align-center flex gap-3", className)}
      ref={ref}
      {...rest}
    >
      {children}
    </div>
  ),
);
CardBody.displayName = "HealthDetails.CardBody";

type CardResultProps = ComponentProps<"h1"> & PropsWithChildren;

export const CardResult = forwardRef<ElementRef<"h1">, CardResultProps>(
  ({ className, children, ...rest }, ref) => (
    <h1
      className={twMerge(
        "pt-1 text-4xl text-secondary-20 dark:text-primary",
        className,
      )}
      ref={ref}
      {...rest}
    >
      {children}
    </h1>
  ),
);
CardResult.displayName = "HealthDetails.CardResult";

export const CardUnit = forwardRef<
  ElementRef<"p">,
  ComponentProps<"p"> & PropsWithChildren
>(({ className, children, ...rest }, ref) => (
  <p
    className={twMerge("text-secondary-20 dark:text-primary", className)}
    ref={ref}
    {...rest}
  >
    {children}
  </p>
));
CardUnit.displayName = "HealthDetails.CardUnit";

type CardDateProps = ComponentProps<"p"> & PropsWithChildren;

export const CardDate = forwardRef<ElementRef<"p">, CardDateProps>(
  ({ className, children, ...rest }, ref) => (
    <p
      className={twMerge(
        "text-xs text-neutral-variant-60 dark:text-neutral-variant-70",
        className,
      )}
      ref={ref}
      {...rest}
    >
      {children}
    </p>
  ),
);
CardDate.displayName = "HealthDetails.CardDate";

type CardDetailsProps = ComponentProps<"div"> & PropsWithChildren;

export const CardDetails = forwardRef<ElementRef<"div">, CardDetailsProps>(
  ({ className, children, ...rest }, ref) => (
    <div
      className={twMerge("flex-col justify-between", className)}
      ref={ref}
      {...rest}
    >
      {children}
    </div>
  ),
);
CardDetails.displayName = "HealthDetails.CardDetails";
